/**
 * Single source of truth for how overlapping, clickable map targets are prioritized.
 *
 * MAP_CLICK_PRIORITY_ORDER lists every clickable target type exactly once, from
 * highest to lowest priority (index 0 wins). The downstream ordering mechanisms (the POI
 * dialog order POI_TYPE_ORDER, router-entity priority ROUTER_ENTITY_DRAWER_PRIORITY,
 * and the stacked Next/Prev navigation) are derived from this one list so priority lives in
 * a single place.
 *
 * This module imports types only. It must never depend on `./constants`
 * (which imports the derived constants from here), so that no import cycle is created.
 */
import type { RouterEntityDrawer } from '../../../constants';
import type {
  DrawerEntityPoiType,
  NavigationalMapClickType,
  PoiType,
  SingletonPoiType,
  StandardPoiType,
} from '../mapping.types';

export type { NavigationalMapClickType };

/**
 * Every clickable map target: routers, POIs, drawer-entity POIs, and navigational
 * short-circuits.
 */
export type MapClickTargetType =
  | RouterEntityDrawer
  | PoiType
  | DrawerEntityPoiType
  | NavigationalMapClickType;

/** Omitting a Union member from list makes the call fail to compile, naming the missing type. */
type ExhaustiveListChecker<Union> = <const T extends readonly Union[]>(
  list: T,
  ..._enforceComplete: [Exclude<Union, T[number]>] extends [never]
    ? []
    : [missingType: Exclude<Union, T[number]>]
) => T;

/** Returns list unchanged, enforcing at compile time that it covers every member of Union. */
const exhaustiveList =
  <Union>(): ExhaustiveListChecker<Union> =>
  (list, ..._enforceComplete) =>
    list;

/**
 * All clickable targets in priority order (index 0 = highest priority). This is the
 * canonical ordering; every other order in the click pipeline is derived from it.
 *
 * The grouping below is documentary only - priority is purely positional:
 *  - navigational short-circuits win outright (they navigate away)
 *  - point routers always short-circuit over POIs
 *  - POI singletons are replace-all selections made outside the map click handler
 *  - standard POIs: deliberately ordered; `alert` (the only polygon router) is
 *    interleaved just below `activeEvacZone`
 *  - drawer-entity POIs come last
 */
export const MAP_CLICK_PRIORITY_ORDER = exhaustiveList<MapClickTargetType>()([
  // Navigational short-circuits.
  'photoMarker',
  'report',
  // Point-like router entity drawers.
  'wildfire',
  'flooding',
  'location',
  'hazard',
  'place',
  'camera',
  'gauge',
  'weatherStation',
  'aqi',
  // Singletons (replace-all).
  'measureTool',
  'aircraft',
  // POIs.
  'externalGeoEvent',
  'lightning',
  'activeEvacZone',
  'alert', // Polygon router entity drawer.
  'evacZone',
  'firePerimeter',
  'legacyPlace',
  'powerOutage',
  'electricalLine',
  'gasPipeline',
  'radioTower',
  'electricRetail',
  'responsibilityArea',
  'femaFloodHazard',
  'ngfsDetection',
  // Drawer-entity POIs.
  'privateLandOwnership',
  'basic',
  'overstory',
]);

// Category tuples. Ordering within each tuple is irrelevant - only inclusion is checked.

export const NAVIGATIONAL_TYPES = exhaustiveList<NavigationalMapClickType>()([
  'photoMarker',
  'report',
]);

export const ROUTER_ENTITY_TYPES = exhaustiveList<RouterEntityDrawer>()([
  'wildfire',
  'flooding',
  'location',
  'hazard',
  'place',
  'camera',
  'gauge',
  'weatherStation',
  'aqi',
  'alert',
]);

export const SINGLETON_POI_TYPES = exhaustiveList<SingletonPoiType>()([
  'measureTool',
  'aircraft',
]);

export const STANDARD_POI_TYPES = exhaustiveList<StandardPoiType>()([
  'lightning',
  'externalGeoEvent',
  'activeEvacZone',
  'evacZone',
  'firePerimeter',
  'legacyPlace',
  'powerOutage',
  'electricalLine',
  'gasPipeline',
  'radioTower',
  'electricRetail',
  'responsibilityArea',
  'femaFloodHazard',
  'ngfsDetection',
]);

export const DRAWER_ENTITY_POI_TYPES = exhaustiveList<DrawerEntityPoiType>()([
  'privateLandOwnership',
  'basic',
  'overstory',
]);

const STANDARD_POI_TYPE_SET = new Set<string>(STANDARD_POI_TYPES);

export const POI_TYPE_ORDER: StandardPoiType[] =
  MAP_CLICK_PRIORITY_ORDER.filter((type): type is StandardPoiType =>
    STANDARD_POI_TYPE_SET.has(type),
  );

const ROUTER_ENTITY_TYPE_SET = new Set<string>(ROUTER_ENTITY_TYPES);

export const ROUTER_ENTITY_DRAWER_PRIORITY = Object.fromEntries(
  MAP_CLICK_PRIORITY_ORDER.filter((type): type is RouterEntityDrawer =>
    ROUTER_ENTITY_TYPE_SET.has(type),
  ).map((entityType, index) => [entityType, index]),
) as Record<RouterEntityDrawer, number>;

const POLICY_INDEX_BY_TYPE = new Map<string, number>(
  MAP_CLICK_PRIORITY_ORDER.map((type, index) => [type, index]),
);

/**
 * POI types that are mutually exclusive: when activeEvacZone is present, evacZone is suppressed.
 */
export const MUTUALLY_EXCLUSIVE_POI_TYPES: Partial<
  Record<MapClickTargetType, MapClickTargetType[]>
> = {
  activeEvacZone: ['evacZone'],
};

/** Priority rank for a target type - lower wins. Unrecognized values sort last. */
export const getPolicyIndex = (type: MapClickTargetType): number =>
  POLICY_INDEX_BY_TYPE.get(type) ?? Number.MAX_SAFE_INTEGER;
